import { DateFormat, formatDate, now } from "common";
import { Dayjs } from "dayjs";
import { PaymentMethod, SelectionState } from "types";
import create from "zustand";
import { AnalyticsMode } from "./analyticsFilterStore";

export interface EarningsReportResponse {
    total: number;
    methods: Array<{ method: PaymentMethod; total: number; paid: number; commission: number }>;
}

export interface EarningsReportFilter {
    products: SelectionState;
    paymentMethods: SelectionState;
    reservationist: SelectionState;
    mode: AnalyticsMode;
}

const defaultFilters: EarningsReportFilter = {
    products: { all: true, selected: [] },
    paymentMethods: { all: true, selected: [] },
    reservationist: { all: true, selected: [] },
    mode: AnalyticsMode.CASH_FLOW,
};

const defaultRange = () => {
    return { from: now().startOf("day"), to: now().endOf("day") };
};

interface EarningsReportStoreState {
    to?: Dayjs;
    from?: Dayjs;
    getFrom: () => Dayjs;
    getTo: () => Dayjs;
    filters: EarningsReportFilter;
    setFilters: (filters: Partial<EarningsReportFilter>) => void;
    resetFilters: (filters?: Partial<EarningsReportFilter>) => void;
    setFrom: (date: Dayjs) => void;
    setTo: (date: Dayjs) => void;
    getDateParams: () => Record<string, string>; // Not sure why `DateParamsProps` does not work
}

export const useEarningsReportFilterStore = create<EarningsReportStoreState>((set, get) => ({
    from: undefined,
    to: undefined,
    filters: defaultFilters,

    getDateParams() {
        const { mode } = get().filters;
        const name = mode === AnalyticsMode.RECOGNIZED ? "items.realizedAt[range]" : "createdAt[range]";
        const params = [
            formatDate(get().getFrom(), DateFormat.DATE_TIME_ISO),
            formatDate(get().getTo(), DateFormat.DATE_TIME_ISO),
        ];
        return { [name]: params.join(",") };
    },

    setFilters(filters: Partial<EarningsReportFilter>) {
        set((state) => ({ filters: { ...state.filters, ...filters } }));
    },

    resetFilters(filters?: Partial<EarningsReportFilter>) {
        set({ filters: { ...defaultFilters, ...filters } });
    },

    getFrom() {
        return get().from ?? defaultRange().from;
    },

    getTo() {
        return get().to ?? defaultRange().to;
    },

    setFrom(from: Dayjs) {
        set(() => ({ from }));
    },

    setTo(to: Dayjs) {
        set(() => ({ to }));
    },
}));
